import { computed, onBeforeUnmount, ref } from 'vue';

/**
 * Composable for managing expandable container behavior with max-height constraints.
 * Provides expand/collapse state management and ResizeObserver-based content measurement.
 * Designed for documentation components that need to clamp content height.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.maxHeightClass - CSS utility class for max-height (e.g., 'd-hmx-500')
 * @param {number} options.heightFudge - Pixels to subtract from threshold for better UX (default: 8)
 * @param {number} options.debounceMs - Debounce delay for resize events (default: 100)
 * @returns {Object} Expandable state and methods
 */
export function useDocExpandable({
  maxHeightClass = 'd-hmx-700',
  heightFudge = 8,
  debounceMs = 100,
} = {}) {
  // Constants
  const DEFAULT_MAX_HEIGHT = 464;

  // State
  const isExpanded = ref(false);
  const isExpandable = ref(false);
  const timers = {
    debounceResize: null,
    debounceWindow: null,
  };

  const observers = {
    resize: null,
  };

  // Resolved max height in pixels, measured from DOM
  const resolvedMaxHeight = ref(DEFAULT_MAX_HEIGHT);

  /**
   * Measure the actual computed max-height from an element.
   * @param {HTMLElement} element - The element with the max-height class applied
   */
  const measureMaxHeight = (element) => {
    if (!element) return;
    const computedStyle = window.getComputedStyle(element);
    const maxH = parseFloat(computedStyle.maxBlockSize || computedStyle.maxHeight);
    if (!Number.isNaN(maxH) && maxH > 0) {
      resolvedMaxHeight.value = maxH;
    }
  };

  /**
   * Calculate the threshold for determining if content is expandable.
   * Subtracts fudge factor for better UX.
   */
  const expandThreshold = computed(() => Math.max(resolvedMaxHeight.value - heightFudge, 0));

  /**
   * Computed property to determine if the expand button should be shown.
   */
  const shouldShowButton = computed(() => !isExpanded.value && isExpandable.value);

  /**
   * Determine whether the content exceeds the max-height threshold.
   * @param {HTMLElement} element - The container element to measure
   */
  const updateExpandable = (element) => {
    if (!element) {
      isExpandable.value = false;
      return;
    }

    const scrollHeight = element.scrollHeight ?? 0;
    isExpandable.value = scrollHeight > expandThreshold.value;
  };

  /**
   * Handle window resize events with debouncing.
   * @param {HTMLElement} element - The container element to measure
   */
  const handleResize = (element) => {
    clearTimeout(timers.debounceWindow);
    timers.debounceWindow = setTimeout(() => {
      updateExpandable(element);
    }, debounceMs);
  };

  /**
   * Initialize ResizeObserver for the given element.
   * @param {HTMLElement} element - The container element to observe
   */
  const initExpandable = (element) => {
    if (!element) {
      return;
    }

    // Measure actual max-height from DOM
    measureMaxHeight(element);

    // Initial measurement
    updateExpandable(element);

    // Set up window resize listener
    const resizeHandler = () => handleResize(element);
    window.addEventListener('resize', resizeHandler, { passive: true });

    // Store the handler for cleanup
    timers.resizeHandler = resizeHandler;

    // Set up ResizeObserver if available
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    observers.resize = new ResizeObserver(() => {
      clearTimeout(timers.debounceResize);
      timers.debounceResize = setTimeout(() => {
        updateExpandable(element);
      }, debounceMs);
    });

    observers.resize.observe(element);
  };

  /**
   * Expand the container by setting isExpanded to true and optionally
   * removing max-height class from provided element(s).
   * @param {HTMLElement|HTMLElement[]} elements - Element(s) to remove class from
   */
  const handleExpand = (elements = null) => {
    isExpanded.value = true;

    if (elements) {
      const elementArray = Array.isArray(elements) ? elements : [elements];
      elementArray.forEach(element => {
        if (element && element.classList) {
          element.classList.remove(maxHeightClass);
        }
      });
    }
  };

  /**
   * Clean up observers and timers.
   * Should be called in onBeforeUnmount.
   */
  const cleanup = () => {
    // Remove window resize listener
    if (timers.resizeHandler) {
      window.removeEventListener('resize', timers.resizeHandler);
    }

    // Clear all timers
    clearTimeout(timers.debounceResize);
    clearTimeout(timers.debounceWindow);

    // Disconnect and clean up ResizeObserver
    if (observers.resize) {
      observers.resize.disconnect();
      observers.resize = null;
    }
  };

  // Auto-cleanup on unmount
  onBeforeUnmount(() => {
    cleanup();
  });

  return {
    // State
    isExpanded,
    isExpandable,

    // Computed
    shouldShowButton,
    expandThreshold,
    resolvedMaxHeight,

    // Methods
    handleExpand,
    updateExpandable,
    initExpandable,
    cleanup,
  };
}
